<template>
  <div class="offline-download-overlay">
    <div class="offline-download-modal">
      <h3>Download offline assets</h3>
      <p>
        Save {{ videoCount }} video asset{{ videoCount === 1 ? '' : 's' }} and {{ imageCount }} image asset{{ imageCount === 1 ? '' : 's' }} for offline use.
      </p>

      <div class="offline-progress-section">
        <div class="offline-progress-label">
          <span>Videos</span>
          <span>{{ videoCompleted }}/{{ videoCount }}</span>
        </div>
        <div class="offline-progress-track">
          <div class="offline-progress-bar is-video" :style="{ width: `${videoPercent}%` }"></div>
        </div>
      </div>

      <div class="offline-progress-section">
        <div class="offline-progress-label">
          <span>Images</span>
          <span>{{ imageCompleted }}/{{ imageCount }}</span>
        </div>
        <div class="offline-progress-track">
          <div class="offline-progress-bar is-image" :style="{ width: `${imagePercent}%` }"></div>
        </div>
      </div>

      <p class="offline-status">{{ statusText }}</p>

      <div class="offline-actions">
        <button v-if="!showClose" type="button" class="btn btn-secondary" :disabled="downloading" @click="$emit('cancel')">
          Not now
        </button>
        <button v-if="!showClose" type="button" class="btn btn-primary" :disabled="downloading" @click="$emit('download')">
          Download
        </button>
        <button v-if="showClose" type="button" class="btn btn-secondary" @click="$emit('close')">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  videoCount: { type: Number, default: 0 },
  imageCount: { type: Number, default: 0 },
  videoCompleted: { type: Number, default: 0 },
  imageCompleted: { type: Number, default: 0 },
  statusText: { type: String, default: 'Ready to download.' },
  downloading: { type: Boolean, default: false },
  showClose: { type: Boolean, default: false },
});

defineEmits(['download', 'cancel', 'close']);

const toPercent = (completed, total) => {
  if (total <= 0) return 100;
  const safeCompleted = Math.min(Math.max(completed || 0, 0), total);
  return Math.round((safeCompleted / total) * 100);
};

const videoPercent = computed(() => toPercent(props.videoCompleted, props.videoCount));
const imagePercent = computed(() => toPercent(props.imageCompleted, props.imageCount));
</script>

<style scoped>
.offline-download-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2400;
  padding: 20px;
}

.offline-download-modal {
  width: min(460px, 92vw);
  background: #fff;
  border-radius: 14px;
  padding: 20px 18px;
  box-shadow: 0 14px 42px rgba(0, 0, 0, 0.22);
  color: #1f2937;
}

.offline-download-modal h3 {
  margin: 0 0 10px;
  font-size: 1.1rem;
  font-weight: 700;
}

.offline-download-modal p {
  margin: 0 0 14px;
  font-size: 0.92rem;
  line-height: 1.35;
}

.offline-progress-section {
  margin-bottom: 10px;
}

.offline-progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  margin-bottom: 5px;
}

.offline-progress-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.offline-progress-bar {
  height: 100%;
  width: 0;
  transition: width 0.2s ease;
}

.offline-progress-bar.is-video {
  background: #2563eb;
}

.offline-progress-bar.is-image {
  background: #059669;
}

.offline-status {
  margin: 10px 0 14px;
  font-size: 0.84rem;
  color: #4b5563;
  min-height: 1.2em;
}

.offline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn {
  padding: 7px 12px;
  border-radius: 7px;
  font-size: 0.85rem;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  border: 0;
  background: #111827;
  color: #fff;
}

.btn-secondary {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
}
</style>
